import { createContext, useCallback, useContext, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { Alert, Snackbar } from '@mui/material'
import type { AlertColor } from '@mui/material'

interface Message {
  text: string
  severity: AlertColor
}

interface SnackbarContextValue {
  notify: (text: string, severity?: AlertColor) => void
}

const SnackbarContext = createContext<SnackbarContextValue>({ notify: () => {} })

/** Avisos breves de resultado de una acción (guardado, error, etc.). */
export function SnackbarProvider({ children }: { children: ReactNode }) {
  const [message, setMessage] = useState<Message | null>(null)

  const notify = useCallback((text: string, severity: AlertColor = 'success') => {
    setMessage({ text, severity })
  }, [])

  const value = useMemo(() => ({ notify }), [notify])

  return (
    <SnackbarContext.Provider value={value}>
      {children}
      <Snackbar
        open={message !== null}
        autoHideDuration={message?.severity === 'error' ? 8000 : 4000}
        onClose={() => setMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {message ? (
          <Alert
            severity={message.severity}
            variant="filled"
            onClose={() => setMessage(null)}
            sx={{ width: '100%' }}
          >
            {message.text}
          </Alert>
        ) : undefined}
      </Snackbar>
    </SnackbarContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export const useSnackbar = () => useContext(SnackbarContext)
